import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import {cn} from '@/lib/utils'
import {rankMatches} from '@/lib/ranking'

/** `cedula` y `status` son opcionales: el selector del afectado lo ve cualquier miembro y nunca
 * recibe la cédula, así que desambigua el área. */
export function PickerOptionRow({
  person,
  testId,
  onPick,
}: {
  person: {name: string; last_name: string; cedula?: string; status?: string; area?: string | null; vinculo?: string}
  testId: string
  onPick: () => void
}) {
  const {t} = useTranslation()
  const secundario = person.cedula ?? person.area
  const inactive = (person.status ?? 'active') !== 'active'
  const externo = person.vinculo === 'externo'
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onPick}
      className="flex min-h-11 w-full items-baseline gap-2 px-3 py-2 text-left text-sm hover:bg-secondary md:min-h-0">
      <span className="min-w-0 truncate font-medium">
        {person.name} {person.last_name}
      </span>
      {secundario && (
        <span className={cn('shrink-0 text-xs text-muted-foreground', person.cedula && 'font-mono')}>{secundario}</span>
      )}
      {(externo || inactive) && (
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {externo && <Tag tone="neutral">{t('plan.externoTag')}</Tag>}
          {inactive && <Tag tone="neutral">{t('employees.status.inactive')}</Tag>}
        </span>
      )}
    </button>
  )
}

function groupNames(employees: Employee[], field: 'areas' | 'positions'): string[] {
  const names = new Set<string>()
  for (const employee of employees) for (const group of employee[field]) names.add(group.name)
  return [...names].sort((a, b) => a.localeCompare(b, 'es'))
}

export function EmployeePicker({
  roster,
  selected,
  onChange,
  lastSelection,
}: {
  roster: Employee[]
  selected: Employee[]
  onChange: (selected: Employee[]) => void
  lastSelection: Employee[]
}) {
  const {t} = useTranslation()
  const [query, setQuery] = useState('')
  const [groupMenu, setGroupMenu] = useState<'areas' | 'positions' | null>(null)
  const actives = useMemo(() => roster.filter((e) => e.status === 'active'), [roster])

  const matches = useMemo(
    () => rankMatches(roster, query, (employee) => selected.some((s) => s.id === employee.id)),
    [roster, query, selected]
  )

  function add(employees: Employee[]) {
    const merged = [...selected]
    for (const employee of employees) if (!merged.some((s) => s.id === employee.id)) merged.push(employee)
    onChange(merged)
    setQuery('')
    setGroupMenu(null)
  }

  function expandGroup(field: 'areas' | 'positions', name: string) {
    // A snapshot of the group's CURRENT active members — never a live link.
    add(actives.filter((e) => e[field].some((g) => g.name === name)))
  }

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((employee) => (
            <button
              key={employee.id}
              type="button"
              data-testid="picker-chip"
              onClick={() => onChange(selected.filter((s) => s.id !== employee.id))}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-accent-foreground hover:opacity-75">
              <span className="min-w-0 truncate">
                {employee.name} {employee.last_name}
              </span>
              <span aria-hidden="true" className="shrink-0 font-normal opacity-60">
                ×
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('evidence.picker.placeholder')}
          className="w-full rounded-lg border border-input bg-transparent px-3 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        />
        {matches.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-border bg-popover shadow-md">
            {matches.map((employee) => (
              <li key={employee.id}>
                <PickerOptionRow person={employee} testId="picker-option" onPick={() => add([employee])} />
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <PickerAction onClick={() => add(actives)}>{t('evidence.picker.allActive')}</PickerAction>
        <PickerAction onClick={() => setGroupMenu(groupMenu === 'areas' ? null : 'areas')}>
          {t('evidence.picker.byArea')}
        </PickerAction>
        <PickerAction onClick={() => setGroupMenu(groupMenu === 'positions' ? null : 'positions')}>
          {t('evidence.picker.byPosition')}
        </PickerAction>
        {lastSelection.length > 0 && (
          <PickerAction onClick={() => add(lastSelection)}>{t('evidence.picker.repeatLast')}</PickerAction>
        )}
      </div>
      {groupMenu && (
        <div className="flex flex-wrap gap-1.5">
          {groupNames(actives, groupMenu).map((name) => {
            const count = actives.filter((e) => e[groupMenu].some((g) => g.name === name)).length
            return (
              <PickerAction key={name} onClick={() => expandGroup(groupMenu, name)} accent>
                {name} ({count})
              </PickerAction>
            )
          })}
          {groupNames(actives, groupMenu).length === 0 && (
            <p className="text-xs text-muted-foreground">{t('evidence.picker.noGroups')}</p>
          )}
        </div>
      )}
    </div>
  )
}

function PickerAction({children, onClick, accent}: {children: React.ReactNode; onClick: () => void; accent?: boolean}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs font-semibold',
        accent
          ? 'border-primary/40 text-primary hover:bg-accent'
          : 'border-input text-muted-foreground hover:text-foreground'
      )}>
      {children}
    </button>
  )
}
